import {
  MutableRefObject,
  RefObject,
  useEffect,
  useLayoutEffect,
  useState,
} from "react";

/**
 * How many of a row's items fit on it whole, and how many are left over.
 *
 * For the places that show a list on one line and count the rest: the chips in
 * a `MultiSelect`, the allergies on a client's Details tab. An item is either
 * shown whole or not shown at all — half a word behind the edge of a field
 * reads as a bug, where "+4" reads as an answer.
 *
 * Which means measuring. The labels come from the backend and a row's width
 * from the viewport, so neither is known where the row is written. The caller
 * renders every item, this measures them against the row, and the caller
 * re-renders with the ones that fit. It happens in a layout effect, before the
 * browser paints, so the full row is never seen.
 *
 * The caller's part of the bargain, for one pass to settle it:
 *
 *   - render all `count` items while `measuring`, each held at its natural
 *     width, and give this their nodes in `itemRefs`
 *   - render the overflow pill at its widest — every item but the first behind
 *     it — so the space kept for it is enough for the number it ends up showing
 *   - hide the row while `measuring`, since that render is not the answer
 */
export interface FitCountOptions {
  /** The row the items are laid out in. Its width is what they must fit.  */
  rowRef: RefObject<HTMLElement | null>;
  /** The rendered items, in order. Only the first `count` are read. */
  itemRefs: MutableRefObject<Array<HTMLElement | null>>;
  /** The "+N" pill, measured so room can be kept for it. */
  overflowRef: RefObject<HTMLElement | null>;
  /**
   * Identifies the items a measurement was taken of — their values joined, or
   * anything else that changes when they do. A measurement is kept only while
   * this and the row's width both hold.
   */
  key: string;
  count: number;
  /** The gap between items, in px. The row's own `gap-*`, in a number. */
  gap: number;
  /**
   * Set where the pill is shown whatever fits — a caller measuring only the
   * first few of a longer list. Without it the last item measured is allowed
   * the whole row, on the reading that nothing follows it.
   */
  hasMore?: boolean;
}

export interface FitCount {
  /** This render is the measuring one: everything is laid out, and hidden. */
  measuring: boolean;
  /** How many to draw. Every item while measuring. */
  shown: number;
  /** What the pill stands for. `0` while measuring, and where all of them fit. */
  hiddenCount: number;
  /** Set where not even one item fits, and that one has to be squeezed. */
  tight: boolean;
}

export function useFitCount({
  rowRef,
  itemRefs,
  overflowRef,
  key,
  count,
  gap,
  hasMore = false,
}: FitCountOptions): FitCount {
  const [fit, setFit] = useState<{
    key: string;
    /** Row width it was measured at — a resize invalidates it. */
    width: number;
    count: number;
    tight: boolean;
  } | null>(null);

  const current = fit?.key === key ? fit : null;

  useLayoutEffect(() => {
    if (current) return;

    const row = rowRef.current;
    if (!row) return;

    const available = row.clientWidth;
    const items = itemRefs.current.slice(0, count);

    // Nothing has been laid out — a jsdom render, or a form inside a closed
    // panel. Every item is shown; the row will be measured when it is real.
    if (!available || items.some((item) => !item)) {
      setFit({ key, width: available, count, tight: false });
      return;
    }

    // The pill is measured at its widest, so the count growing a digit later
    // cannot push an item off the edge.
    const overflowWidth = overflowRef.current?.offsetWidth ?? 0;

    let used = 0;
    let fitted = 0;
    items.forEach((item, index) => {
      if (fitted < index) return; // an item has already been dropped
      const next = used + (index === 0 ? 0 : gap) + item!.offsetWidth;
      // Everything but the last item has to leave room for the pill that will
      // stand for whatever comes after it — and the last one too, where the
      // caller has more items than it gave this to measure.
      const room =
        index === items.length - 1 && !hasMore
          ? available
          : available - gap - overflowWidth;
      if (next > room) return;
      used = next;
      fitted = index + 1;
    });

    setFit({
      key,
      width: available,
      // A row too narrow for even one item still shows one, squeezed: an
      // empty-looking row beside a "+6" says less than a name does.
      count: count > 0 ? Math.max(fitted, 1) : 0,
      tight: count > 0 && fitted === 0,
    });
  }, [current, key, count, gap, hasMore, rowRef, itemRefs, overflowRef]);

  // A row that is narrowed — the window, or the grid it sits in dropping to
  // one column — is measured again for its new width.
  useEffect(() => {
    const row = rowRef.current;
    if (!row || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      setFit((taken) => (taken && taken.width === row.clientWidth ? taken : null));
    });
    observer.observe(row);
    return () => observer.disconnect();
  }, [rowRef]);

  return {
    measuring: !current,
    shown: current ? current.count : count,
    hiddenCount: current ? count - current.count : 0,
    tight: current?.tight ?? false,
  };
}

export default useFitCount;
